import Box from "@mui/material/Box";
import ErrorSnackBar from "../CommonComponents/ErrorSnackBar/ErrorSnackBar";
import StyledConfirmationDialog from "../CommonComponents/StyledConfirmitationDialog/StyledConfirmationDialog";
import StyledPopup from "../CommonComponents/StyledPopup/StyledPopup";
import {Organization, Team, User} from "../../types/users";
import TransparentButton from "../CommonComponents/TransparentButton/TransparentButton";
import {DataGrid, GridColDef, GridRenderCellParams} from "@mui/x-data-grid";
import React, {useEffect, useState} from "react";
import {defaultOrganization, EntityType, organizationColumns} from "../../utils/constants";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {useTheme} from "../../contexts/theme";
import {Competition} from "../../types/competitionTypes";
import OrganizationForm from "./OrganizationForm";
import {
    createOrganization,
    deleteOrganization,
    getOrganizations,
    getTeams,
    getUsers,
    updateOrganization
} from "../../queries/userQueries";
import {getCompetitions} from "../../queries/competitionQueries";
import SpinnerBox from "../CommonComponents/SpinnerBox/SpinnerBox";

interface OrganizationTableProps{
    updateEntity : EntityType.users | EntityType.organizations | "";
    handleUpdateEntity : (value: EntityType.users | EntityType.organizations | "") => void;
}

const OrganizationTable: React.FC<OrganizationTableProps> = ({updateEntity,handleUpdateEntity}) => {

    const theme = useTheme();

    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganization, setLoadingOrganization] = useState<boolean>(true);
    const [loadingForm, setLoadingForm] = useState<boolean>(true);
    const [users, setUsers] = useState<User[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [competitions, setCompetitions] = useState<Competition[]>([]);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formMode, setFormMode] = useState<'create'| 'update'>('create')
    const [selectedOrganization, setSelectedOrganization] = useState<Organization>(JSON.parse(JSON.stringify(defaultOrganization)));
    const [tableColumns, setTableColumns] = useState<GridColDef<Organization>[]>([]);
    const [errorMessage, setErrorMessage] = useState<{error: boolean, message: string}>({
        error:false,
        message: "",
    })
    const [openDialogDeleteOrganization, setOpenDialogDeleteOrganization] = useState<boolean>(false);

    async function loadOrganizations(){
        try{
            setLoadingOrganization(true)
            const organizations = await getOrganizations()
            setOrganizations(organizations);
            setLoadingOrganization(false)
        }catch (e){
            console.error(e)
            setLoadingOrganization(false)
            setErrorMessage({error: true, message: "Problems retrieving Organizations"})
        }
    }

    async function loadUsers(){
        try{
            const users = await getUsers()
            setUsers(users);
        }catch (e) {
            console.error(e)
            setErrorMessage({error: true, message: "Failed to load users Options for organizations"})
        }
    }

    async function loadCompetitions(){
        try{
            const competitions = await getCompetitions()
            setCompetitions(competitions);
        }catch (e) {
            console.error(e)
            setErrorMessage({error: true, message: "Failed to load users Options for organizations"})
        }
    }

    async function handleOrganizationChange(name: keyof Organization, value: any){
        setSelectedOrganization(prevState => ({...prevState, [name]: value}))
    }

    async function loadTeams(id_competitions: string[]) {
        try{
            const response = await getTeams(id_competitions);
            const sortedTeams = response.sort((a: Team, b: Team) => a.name.localeCompare(b.name));
            setTeams(sortedTeams);
        }catch (errorTeams){
            console.error(errorTeams)
            setErrorMessage({error: true, message: "Failed to load Teams for organizations"})
        }
    }


    useEffect(() => {
        loadOrganizations()
        addActionColumns()
    }, []);

    async function loadDropDownData() {
        const promises = [loadCompetitions(), loadUsers()];
        await Promise.all(promises);
        setLoadingForm(false);
    }



    useEffect(() => {
        if (isFormOpen)
            loadDropDownData()
    }, [isFormOpen]);

    useEffect(() => {
        if (updateEntity === EntityType.users) {
            loadOrganizations()
            loadUsers()
            loadCompetitions()
            addActionColumns()
            handleUpdateEntity("")
        }
    }, [updateEntity]);

    useEffect(() => {

        if (selectedOrganization.competitions.length > 0){

            let id_competitions: string[] = [];

            selectedOrganization.competitions.map((item, index)=>{
                id_competitions.push(item.id);
            })

            loadTeams(id_competitions)
        }

    }, [selectedOrganization]);



    function handleClose(){
        setIsFormOpen(false);
        setSelectedOrganization(JSON.parse(JSON.stringify(defaultOrganization)));
        setFormMode("create")
    }

    async function onUpdateClick(organization: Organization){
        setSelectedOrganization(organization)
        setIsFormOpen(true)
        setFormMode("update")
    }

    async function handleFormSubmit(){
        try{
            if (formMode === 'create'){
                const tempSelectedOrganization = {...selectedOrganization};
                const createdOrganizationId: {id: string} = await createOrganization(tempSelectedOrganization);
                tempSelectedOrganization.id = createdOrganizationId.id;
                setOrganizations(prevState => [...prevState, JSON.parse(JSON.stringify(tempSelectedOrganization))])
            }else if (formMode === 'update'){
                await updateOrganization(selectedOrganization)
                const tempOrgs = [...organizations]
                const updatedOrgIndex = tempOrgs.findIndex(user => user.id === selectedOrganization.id)
                if (updatedOrgIndex > -1) {
                    tempOrgs[updatedOrgIndex] = (selectedOrganization)
                    setOrganizations(tempOrgs)
                }
                handleUpdateEntity(EntityType.organizations)
            }



            handleClose()
            setErrorMessage({error: true, message: `Organization ${formMode === 'create' ? 'created' : 'updated'}  successfully`})
        }catch (e: any) {
            if(e.response && e.response.data && e.response.data.detail){
                console.error(e)
                setErrorMessage({error: true, message: `Error ${formMode === 'create' ? 'creating' : 'updating'} organization: ${e.response.data.detail.message}`})
            }else{
                console.error(e)
                setErrorMessage({error: true, message: `Error ${formMode === 'create' ? 'creating' : 'updating'}  organization`})
            }
        }
    }


    function onDeleteClick(organization: Organization){
        setSelectedOrganization(organization);
        setOpenDialogDeleteOrganization(true);
    }

    async function handleDeleteOrganization() {
        try{
            await deleteOrganization(selectedOrganization.id)
            setOrganizations(prevState => prevState.filter(org => org.id !== selectedOrganization.id))
            setErrorMessage({error: true, message: "Organization Deleted Successfully"})
        }catch (e) {
            console.error(e)
            setErrorMessage({error: true, message: "Organization Delete Failed"})
        }
    }


    function onCreateClick(){
        setIsFormOpen(true)
        setFormMode("create")
    }



    function addActionColumns(){
        const tempColumns =  [...organizationColumns]
        tempColumns.push(
            {
                field: "actions",
                headerName: "",
                flex: 1,
                renderCell: (params: GridRenderCellParams<Organization>) => (
                    <Box paddingTop={theme.spacing.xs} width={'100%'} display={'flex'} justifyContent={'space-around'} alignItems={'center'}>
                        <TransparentButton
                            onClick={() => onUpdateClick(params.row)}
                            startIcon={<EditIcon/>}
                            text={''}
                        />

                        <TransparentButton
                            onClick={() => onDeleteClick(params.row)}
                            startIcon={<DeleteIcon/>}
                            text={''}
                        />
                    </Box>
                ),
            },
        )
        setTableColumns(tempColumns)
    }


    return (
        <Box>
            <ErrorSnackBar  autoHideDuration={5000} open={errorMessage.error} setClose={()=>{setErrorMessage({
                error: false,
                message: ""
            })}} errorMessage={errorMessage.message} />
            <StyledConfirmationDialog
                open={openDialogDeleteOrganization}
                onClose={() =>  setOpenDialogDeleteOrganization(false)}
                message={`Are you sure you want to delete ${selectedOrganization.name}?`}
                onConfirm={() => handleDeleteOrganization()}
                onDeny={() =>  setOpenDialogDeleteOrganization(false)}
            />
            <StyledPopup
                isOpen={isFormOpen}
                onClose={handleClose}
                title={`${formMode==='create' ? 'Create Organization' : 'Update Organization'}`}
                fullWidth={false}
                width={'65vw'}
            >

                {loadingForm ? (
                    <SpinnerBox  height={'100%'} text={'Loading...'} />
                ):(
                    <OrganizationForm
                        users={users}
                        organization={selectedOrganization}
                        sendFormLabel={formMode === 'create'? 'Create Organization' : 'Update Organization'}
                        competitions={competitions}
                        onFieldChange={handleOrganizationChange}
                        onSubmit={handleFormSubmit}
                        teams={teams}
                    />
                )}

            </StyledPopup>
                <TransparentButton text={'Create Organization'} onClick={onCreateClick}/>
                <DataGrid
                    rows={organizations}
                    columns={tableColumns}
                    getRowId={(row) => row.id}
                    loading={loadingOrganization}
                    pageSizeOptions={[5, 25, 50]}
                    columnVisibilityModel={{
                        id:false
                    }}
                    sx={{
                        border: `2px solid ${theme.palette.white}`,
                        color: theme.palette.white,
                        backgroundColor: theme.palette.blue,
                        marginTop: theme.spacing.md,
                        '& .MuiDataGrid-main': { backgroundColor: theme.palette.blue },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: theme.palette.blue },
                        '& .MuiDataGrid-columnHeader': { backgroundColor: theme.palette.blue },
                        '& .MuiDataGrid-row': { backgroundColor: theme.palette.blue },
                        '& .MuiDataGrid-footerContainer': { backgroundColor: theme.palette.blue },
                        '&.MuiDataGrid-root': {
                            '--DataGrid-containerBackground': theme.palette.blue,
                        },
                        '& .MuiTablePagination-root': {
                            color: theme.palette.white, // Override text color
                        },
                        '& .MuiTablePagination-selectIcon': {
                            color: theme.palette.white, // Change the color of the dropdown icon
                        },
                    }}
                />
        </Box>)
}

export default OrganizationTable